import api from '@/helpers/api_helper';
import { apiUrls } from '@/constant/configure';
import type {
    AutomationLimit,
    AutomationListResult,
    AutomationRefillResult,
    AutomationUsageResult,
    AutomationVerification,
    RequestVerificationReq,
    CreateDmAutomationReq,
    DmAutomationDto,
    DmAutomationStatsDto,
    DmSendLogPage,
    StatsOverviewDto,
    StatsPeriod,
    UpdateDmAutomationReq,
} from '@/types/dmAutomation';

const studio = { baseURL: apiUrls.studio };
const PREFIX = '/api/v1';

export const getAutomations = async (): Promise<AutomationListResult> => {
    const res = await api.get<{ itemList: DmAutomationDto[]; limit?: AutomationLimit }>(`${PREFIX}/automations`, studio);
    return { itemList: res.data.itemList ?? [], limit: res.data.limit ?? null };
};

/** 상한/잔여만 가볍게 조회(목록 미포함). "새 자동화 만들기" 게이팅용. */
export const getAutomationLimit = async (): Promise<AutomationLimit> => {
    const res = await api.get<AutomationLimit>(`${PREFIX}/automations/limit`, studio);
    return res.data;
};

/** 이용권 통합 내역 — 요약 + 충전(+N)/사용(−1)/리필(+N) 타임라인(한 페이지용). */
export const getAutomationUsage = async (): Promise<AutomationUsageResult> => {
    const res = await api.get<AutomationUsageResult>(`${PREFIX}/automations/usage`, studio);
    return { summary: res.data.summary, itemList: res.data.itemList ?? [] };
};

/** 무한리필 — 남은 개수를 (refillTo + 유료)로 복구. 바디 없음. 이벤트 ON 일 때만. */
export const refillAutomationPass = async (): Promise<AutomationRefillResult> => {
    const res = await api.post<AutomationRefillResult>(`${PREFIX}/automations/refill`, undefined, studio);
    return res.data;
};

/**
 * 오토DM 인증 — studio 가 판정·저장. 클라는 상태만 read 해서 화면 분기(판정/차감 금지).
 * 참고: flowing_api_mall/doc/insta승인/오토DM인증_파이프라인_계약_AI용.md
 */
export const getAutomationVerification = async (): Promise<AutomationVerification> => {
    const res = await api.get<AutomationVerification>(`${PREFIX}/automations/verification`, studio);
    return res.data;
};

/** 인증 요청 — { method, proof? }. 요청하면 무조건 PENDING(자동확인 없음, 관리자 수동 승인). */
export const requestAutomationVerification = async (
    body: RequestVerificationReq,
): Promise<AutomationVerification> => {
    const res = await api.post<AutomationVerification>(`${PREFIX}/automations/verification`, body, studio);
    return res.data;
};

export const createAutomation = async (body: CreateDmAutomationReq): Promise<void> => {
    await api.post(`${PREFIX}/automations`, body, studio);
};

export const updateAutomation = async (id: string, body: UpdateDmAutomationReq): Promise<void> => {
    await api.put(`${PREFIX}/automations/${id}`, body, studio);
};

export const toggleAutomation = async (id: string, isActive: boolean): Promise<void> => {
    await api.patch(`${PREFIX}/automations/${id}/toggle`, { isActive }, studio);
};

export const deleteAutomation = async (id: string): Promise<void> => {
    await api.delete(`${PREFIX}/automations/${id}`, studio);
};

export const getStatsOverview = async (): Promise<StatsOverviewDto> => {
    const res = await api.get<{ item: StatsOverviewDto }>(`${PREFIX}/automations/stats/overview`, studio);
    return res.data.item;
};

export const getAutomationStats = async (
    id: string,
    period: StatsPeriod = 'all',
): Promise<DmAutomationStatsDto> => {
    const res = await api.get<{ item: DmAutomationStatsDto }>(`${PREFIX}/automations/${id}/stats`, {
        ...studio,
        params: { period },
    });
    return res.data.item;
};

export const getAutomationLogs = async (
    id: string,
    page = 1,
    limit = 20,
): Promise<DmSendLogPage> => {
    const res = await api.get<DmSendLogPage>(`${PREFIX}/automations/${id}/logs`, {
        ...studio,
        params: { page, limit },
    });
    return res.data;
};
